import type React from 'react';
import { Heart } from 'lucide-react';

/**
 * A persistent, quiet mental-health support panel that sits between page
 * content and the footer site-wide. Tone: warm, matter-of-fact, never a
 * disclaimer or banner.
 */
export function MentalHealthSupportPanel() {
  const wrap: React.CSSProperties = {
    background: '#f3efe6',
    borderTop: '1px solid var(--rule)',
    borderBottom: '1px solid var(--rule)',
    padding: 'clamp(1.6rem, 4vw, 2.4rem) 1rem',
    marginTop: 'clamp(2.5rem, 7vw, 4rem)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
  };

  const inner: React.CSSProperties = {
    maxWidth: 960,
    margin: '0 auto',
  };
  const eyebrow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: 'var(--font-sub)',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--rivergum)',
    margin: 0,
    marginBottom: '0.35rem',
  };
  const heading: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.15rem',
    color: 'var(--ink)',
    margin: 0,
    marginBottom: '0.25rem',
    letterSpacing: '0.01em',
  };
  const intro: React.CSSProperties = {
    fontSize: '0.92rem',
    color: 'var(--ink-mid)',
    margin: 0,
    marginBottom: '0.95rem',
    lineHeight: 1.5,
    maxWidth: 640,
  };
  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '0.55rem',
  };
  const item: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    padding: '0.65rem 0.8rem',
    background: 'var(--warm-white, #fff)',
    border: '1px solid var(--rule)',
    borderRadius: 8,
    textDecoration: 'none',
    color: 'var(--ink)',
    minHeight: 64,
    justifyContent: 'center',
  };
  const name: React.CSSProperties = {
    fontFamily: 'var(--font-sub)',
    fontWeight: 700,
    fontSize: '0.9rem',
    color: 'var(--rivergum)',
    letterSpacing: '0.01em',
  };
  const number: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1rem',
    color: 'var(--ink)',
    letterSpacing: '0.02em',
  };
  const sub: React.CSSProperties = {
    fontSize: '0.78rem',
    color: 'var(--ink-mid)',
    lineHeight: 1.35,
  };

  const services: {
    name: string;
    phone: string;
    phoneHref: string;
    sub: string;
    href?: string;
  }[] = [
    {
      name: 'Lifeline',
      phone: '13 11 14',
      phoneHref: 'tel:131114',
      sub: '24/7 crisis support and suicide prevention.',
      href: 'https://www.lifeline.org.au/',
    },
    {
      name: 'Beyond Blue',
      phone: '1300 22 4636',
      phoneHref: 'tel:1300224636',
      sub: '24/7 support for anxiety, depression and stress.',
      href: 'https://www.beyondblue.org.au/',
    },
    {
      name: '13YARN',
      phone: '13 92 76',
      phoneHref: 'tel:139276',
      sub: 'For mob — a yarn with an Aboriginal or Torres Strait Islander Crisis Supporter, 24/7.',
      href: 'https://www.13yarn.org.au/',
    },
    {
      name: 'Rurallink (WA)',
      phone: '1800 552 002',
      phoneHref: 'tel:1800552002',
      sub: 'After-hours mental health support for rural WA — weekday evenings (4:30pm to 8:30am) and all weekend. During business hours, call HealthDirect on 1800 022 222.',
      href: 'https://www.mhc.wa.gov.au/getting-help/helplines/rurallink/',
    },
    {
      name: 'eheadspace',
      phone: '1800 650 890',
      phoneHref: 'tel:1800650890',
      sub: 'Free chat &amp; phone counselling for 12–25s, 9am–1am AEST, 7 days.',
      href: 'https://headspace.org.au/eheadspace/',
    },
  ];

  return (
    <aside aria-label="Mental health support" style={wrap}>
      <div style={inner}>
        <p style={eyebrow}>
          <Heart size={13} aria-hidden="true" />
          Mental health support
        </p>
        <h2 style={heading}>If today is heavy, here's who to call.</h2>
        <p style={intro}>
          These services are free, confidential, and used by people across
          Warren-Blackwood every day. You don't need a crisis to ring — a hard
          week is reason enough.
        </p>

        <div style={grid}>
          {services.map((s) => (
            <a key={s.name} href={s.phoneHref} style={item}>
              <span style={name}>{s.name}</span>
              <span style={number}>{s.phone}</span>
              <span
                style={sub}
                dangerouslySetInnerHTML={{ __html: s.sub }}
              />
            </a>
          ))}
        </div>

        <p
          style={{
            ...sub,
            marginTop: '0.9rem',
            marginBottom: 0,
            maxWidth: 720,
          }}
        >
          In a life-threatening emergency phone <a href="tel:000" style={{ color: 'var(--rivergum)', fontWeight: 700 }}>000</a>. For non-urgent
          health advice 24/7, HealthDirect on <a href="tel:1800022222" style={{ color: 'var(--rivergum)', fontWeight: 600 }}>1800 022 222</a>.
        </p>
      </div>
    </aside>
  );
}
